import React, { useContext, useMemo } from 'react';
import TouchableOpacityLinker from '../../../components/TouchableOpacityLinker/TouchableOpacityLinker.tsx';
import FotoscapeLink from '../../../components/FotoscapeLink/FotoscapeLink.tsx';
import { adjustFotoscapesLink, EnhancedLightboxItem, } from '../../net/softbox-types.ts';
import { IInterestResponse } from '@pages/Root/services/api/interest/interest-model-definitions.ts';
import { LayoutConfigurationPage } from '../LayoutConfiguration/layout-configuration-factory.ts';
import { createFeedComponent } from './FeedComponentFactory.tsx';
import { createHeaderComponent } from './HeaderComponentFactory.tsx';
import Outbrain from '../../components/Outbrain/Outbrain.tsx';
import { resolveOutbrainWidgetId } from '../../components/Outbrain/constants.ts';
import { useSearchParams } from 'react-router';
import { filterLayoutConfiguration, onItemClicked, } from '@/moment/utils/feed-util.ts';
import { AdCounterContext } from '@moment/context/AdCounterContext.tsx';
import { useTranslation } from 'react-i18next';
import { appendQuerystringParams } from '@moment/layouts/MainFeed/utils.ts';
import { createAdComponent } from '@moment/shared-code/ad-utils.tsx';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { TripodContext } from '@moment/context/TripodContext.tsx';
import { isPartnerExternalFotoscapeLink } from '@moment/utils/runtime-config-util.ts';

interface FeedProps {
  newsItems: EnhancedLightboxItem[];
  interests: IInterestResponse;
  configuration: LayoutConfigurationPage;
  partner?: string;
  source?: string;
  sourceSchedule?: string;
}

export const FeedBuilder: React.FC<FeedProps> = ({
  newsItems,
  interests,
  configuration,
  partner = '',
  source,
  sourceSchedule = 'unapplicable',
}) => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const analytics = useAnalytics();
  const context = useContext(CommonPageDataContext);
  const lang = i18n.language;
  const { tripodData, isLoading: isLoadingTripodData } =
    useContext(TripodContext) || {};

  const pageParams = useMemo(() => {
    return {
      medium: searchParams.get('medium') || 'inapp',
    };
  }, [searchParams]);
  const { getAndIncrement } = useContext(AdCounterContext);

  const useFotoscapeExternalLink = useMemo(() => {
    if (!isLoadingTripodData && !tripodData?.runtimeConfig) {
      return false;
    }
    return isPartnerExternalFotoscapeLink(tripodData, partner);
  }, [isLoadingTripodData, tripodData, partner]);

  const renderFeed = useMemo(() => {
    const feedComponents = configuration.layoutConfiguration
      .filter(filterLayoutConfiguration(pageParams))
      .map((slot, index) => {
        if (slot.type === 'ad') {
          // Ad rendering logic uses shared utility
          const adUnitIndex = getAndIncrement();
          const adKey = `fb-ad-${index}`;
          const placement = adUnitIndex === 1 ? 'top' : 'bot';

          return createAdComponent({
            adUnitId: `feed-${adUnitIndex}`,
            placement,
            containerClassName: 'ad--block',
            bannerClassName: 'ad--title',
            adBlockClassName: 'feed__ad',
            key: adKey,
          });
        } else if (slot.type === 'item') {
          const { component } = createFeedComponent(
            slot,
            newsItems,
            interests,
            TouchableOpacityLinker,
            (item) => {
              const contentLink = ['article', 'standard'].includes(item.lbtype)
                ? appendQuerystringParams(
                    item.linkToContent(partner),
                    searchParams
                  )
                : item.linkToContent(partner);
              let link = useFotoscapeExternalLink
                ? contentLink
                : adjustFotoscapesLink(contentLink);

              // Add schedule parameter to the link if it exists
              if (configuration.schedule) {
                try {
                  const url = new URL(link);
                  url.searchParams.set('schedule', configuration.schedule);
                  link = url.toString();
                } catch (e) {
                  // If URL parsing fails, append schedule as query param manually
                  const separator = link.includes('?') ? '&' : '?';
                  link = `${link}${separator}schedule=${encodeURIComponent(configuration.schedule)}`;
                }
              }

              return {
                ClickerEl: FotoscapeLink,
                clickerElProps: {
                  to: link,
                  className: 'moment-link',
                  onClick: onItemClicked(
                    item,
                    analytics,
                    context.partner || '',
                    link,
                    lang,
                    searchParams,
                    configuration.schedule || '',
                    source,
                    sourceSchedule
                  ),
                },
              };
            },
            i18n,
            source,
            configuration.schedule
          );
          return (
            <React.Fragment key={`fb-${index}`}>{component}</React.Fragment>
          );
        } else if (slot.type === 'header') {
          return createHeaderComponent(slot, index);
        } else if (slot.type === 'outbrain') {
          return (
            <Outbrain
              key={`fb-${index}`}
              widgetId={resolveOutbrainWidgetId(slot.settings?.widgetId)}
              hideHeader={Boolean(slot.settings?.hideHeader)}
            />
          );
        }
        return null; // Skip if no news item is available
      })
      .filter((component) => {
        return component !== null;
      }); // Remove null entries

    return { feedComponents };
  }, [
    configuration,
    i18n,
    interests,
    newsItems,
    partner,
    pageParams,
    searchParams,
    getAndIncrement,
    useFotoscapeExternalLink,
  ]);

  return (
    <div className="feed-container" key={`feed-main`}>
      {renderFeed.feedComponents}
    </div>
  );
};
